import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Compass, Target, Heart, Briefcase, Zap, Award, Brain, Star,
  Users, PenTool, GraduationCap, ChevronRight, ChevronDown,
  MessageCircle, Calendar, CheckCircle, FolderOpen, Play,
  ArrowRight, Sparkles, Trophy, FileText, LogIn, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

const HowToUsePage = () => {
  const navigate = useNavigate();
  const [selectedModel, setSelectedModel] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const guideRef = useRef(null);

  const selectModel = (id) => {
    setSelectedModel(id);
    setExpandedSection('getting-started');
    setTimeout(() => {
      guideRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const MODELS = [
    { id: 'syep', name: 'SYEP', desc: 'Summer Youth Employment Program', icon: '☀️', color: '#E86C3D', tagline: 'Track your summer work experience, earn points, and build your career' },
    { id: 'gps', name: 'Student Pathways GPS', desc: 'For School Districts', icon: '🎓', color: '#E86C3D', tagline: 'Explore careers, set goals, and plan your future after high school' },
    { id: 'youth_dev', name: 'Youth Development', desc: 'Community Programs', icon: '🌱', color: '#10B981', tagline: 'Grow your skills, discover your strengths, and build confidence' },
    { id: 'leadership', name: 'Youth Leadership', desc: 'Leadership Programs', icon: '👑', color: '#7C3AED', tagline: 'Develop your leadership skills and make an impact in your community' },
    { id: 'workforce', name: 'Workforce Development', desc: 'Career Readiness', icon: '💼', color: '#0F766E', tagline: 'Build job-ready skills, explore careers, and prepare for your next opportunity' },
  ];

  const GPS_SECTIONS = [
    {
      id: 'home', title: 'Home', icon: Compass, color: '#E86C3D',
      description: 'Your home base. See your progress, weekly tasks, and AI guidance.',
      features: [
        { name: 'AI Guide', desc: 'Your personal AI mentor appears at the top of every page. It knows your interests, skills, and goals — and tells you exactly what to do next.' },
        { name: 'Pathway Progress', desc: 'A progress bar showing how far you\'ve come. Fill it up by adding interests, skills, goals, careers, and portfolio items.' },
        { name: 'This Week\'s Moves', desc: 'Three quick tasks to keep you on track: write a journal entry, review a goal, explore a career. Check them off to earn points.' },
        { name: 'Quick Actions', desc: 'One-tap access to AI Career Guide, Set a Goal, Ask Counselor, and Journal.' },
      ]
    },
    {
      id: 'discover', title: 'Discover', icon: Heart, color: '#EC4899',
      description: 'Figure out who you are — your interests, skills, activities, and thoughts.',
      features: [
        { name: 'Interests', desc: 'Select topics you\'re interested in: technology, music, healthcare, business, arts, and more. This helps the AI find careers that fit YOU.' },
        { name: 'Skills', desc: 'Rate your skills: communication, teamwork, problem-solving, creativity, and more. Be honest — this helps us help you.' },
        { name: 'My Activities', desc: 'Add clubs, sports, jobs, volunteering, and other things you do. Each activity shows colleges and employers what you\'re about.' },
        { name: 'Micro-Journal', desc: 'Quick reflection prompts. Tap a prompt, write your thoughts, pick your mood. Takes 2 minutes and earns you points.' },
        { name: 'YAS Courses', desc: 'Interactive self-discovery courses: Who Am I, Career Vision Board, Goal Setting, Communication Skills. Complete them for points and badges.' },
      ]
    },
    {
      id: 'explore', title: 'Explore', icon: Briefcase, color: '#8B5CF6',
      description: 'Discover careers that match who you are. The AI does the matching — you decide what excites you.',
      features: [
        { name: 'AI Career Match', desc: 'Tap "Find My Career Matches" and the AI analyzes your interests, skills, and activities to suggest careers you might not have thought of. Each comes with salary, education needed, and skills required.' },
        { name: 'Save / Not For Me', desc: 'For each career the AI suggests, you can Save it to your Career Board or skip it. Saved careers are visible to your counselor and parents.' },
        { name: 'Career Clusters', desc: 'Browse careers by category: Technology, Healthcare, Business, Creative Arts, Education, Trades, Public Service, Science.' },
        { name: 'My Career Board', desc: 'All the careers you\'ve saved in one place. This shows your counselor what directions you\'re interested in.' },
      ]
    },
    {
      id: 'plan', title: 'Plan', icon: Target, color: '#10B981',
      description: 'Turn your ideas into action. Set goals, plan for after high school.',
      features: [
        { name: 'My Goals', desc: 'Set SMART goals (Specific, Measurable, Achievable, Relevant, Timed). The step-by-step wizard walks you through it. When you complete a goal, it auto-posts to your portfolio and you earn points.' },
        { name: 'After High School', desc: 'Track colleges, trade schools, apprenticeships, or military options you\'re interested in. Track application status (Researching → Applied → Accepted → Enrolled).' },
        { name: 'Ask Counselor', desc: 'Send questions directly to your school counselor through the Help Desk. They\'ll answer and you\'ll get a notification.' },
        { name: 'Resources', desc: 'Guides, documents, and links your school has uploaded for you.' },
      ]
    },
    {
      id: 'portfolio', title: 'Portfolio', icon: Award, color: '#F59E0B',
      description: 'Your story, all in one place. Everything you do on the platform builds your portfolio automatically.',
      features: [
        { name: 'Your Identity Card', desc: 'A visual summary of who you are: points earned, interests, skills, goals completed, careers explored.' },
        { name: 'Who I Am', desc: 'Your interests and skills displayed as tags — instantly shows what you\'re about.' },
        { name: 'Where I\'m Headed', desc: 'Careers you\'ve saved and post-secondary schools you\'re researching.' },
        { name: 'My Journey', desc: 'A timeline of your milestones: goals completed, courses finished, wins posted.' },
        { name: 'AI Resume Builder', desc: 'Tap "Generate Resume" and the AI writes a professional summary using your actual profile data. The more you add, the better your resume.' },
        { name: 'Digital Vault', desc: 'Upload certificates, projects, work samples, and achievements. Build a collection you can share.' },
      ]
    },
    {
      id: 'ai', title: 'AI Guide', icon: Brain, color: '#7C3AED',
      description: 'Your personal mentor that knows everything about you and gives guidance based on YOUR specific situation.',
      features: [
        { name: 'My Breakdown', desc: 'See your Identity Map — how complete your profile is across 6 areas (Interests, Skills, Goals, Careers, Activities, Portfolio). Plus "What I Know About You" — all your data in one view.' },
        { name: 'Chat', desc: 'Talk to the AI about anything: careers, goals, feeling lost, what to do next. It knows your full profile and gives personalized advice.' },
        { name: 'Quick Questions', desc: 'Pre-written questions like "What careers would be good for someone like me?" or "I feel lost and don\'t know what I want to do." Tap one to start a conversation.' },
      ]
    },
  ];

  const SYEP_SECTIONS = [
    {
      id: 'dashboard', title: 'Your Dashboard', icon: Compass, color: '#E86C3D',
      description: 'Your home base for the summer. See your points, streak, and what needs to be done.',
      features: [
        { name: 'Points & Streak', desc: 'Track your total points and consecutive active days. Points come from check-ins, goals, wins, and activities.' },
        { name: 'AI Guide', desc: 'Personalized tips about your summer job progress. Knows your worksite, goals, and wins.' },
        { name: 'Quick Actions', desc: 'One-tap buttons for Interests, Skills, AI Guide, Vault, Summer Project, Resources, and Help Desk.' },
        { name: 'Work Journal', desc: 'Share your weekly wins and highlights from your worksite.' },
        { name: 'This Week', desc: 'Weekly tasks: Skills Check, Check-in, and Trivia Quiz. Complete them for points.' },
      ]
    },
    {
      id: 'checkin', title: 'Weekly Check-in', icon: Calendar, color: '#3B82F6',
      description: 'Every week, share how your summer job is going. Takes 2 minutes.',
      features: [
        { name: 'Mood Check', desc: 'How are you feeling this week? Pick your mood.' },
        { name: 'Highlight', desc: 'What was the best part of your week?' },
        { name: 'Challenge', desc: 'What was hard? This helps your Lead Monitor support you.' },
        { name: 'Learning', desc: 'What did you learn? Every week you\'re building new skills.' },
      ]
    },
    {
      id: 'goals', title: 'Goals', icon: Target, color: '#10B981',
      description: 'Set summer goals and long-term goals. Complete them for big points.',
      features: [
        { name: 'SMART Goal Wizard', desc: 'Step-by-step guide: Title, Specific, Measurable, Achievable, Relevant, Deadline. Makes your goals real.' },
        { name: 'Complete Goals', desc: 'When you finish a goal, mark it complete. It auto-posts to the Wins Wall and earns you 25-50 points.' },
      ]
    },
    {
      id: 'wins', title: 'Wins Wall', icon: Trophy, color: '#F59E0B',
      description: 'The social feed where you and your peers celebrate accomplishments.',
      features: [
        { name: 'Post Wins', desc: 'Share what you\'ve accomplished. Completed a project? Learned a skill? Post it.' },
        { name: 'Celebrate', desc: 'Tap to celebrate your teammates\' wins. They see it and it means a lot.' },
        { name: 'Comment', desc: 'Leave encouragement on others\' posts. Build each other up.' },
      ]
    },
    {
      id: 'leaderboard', title: 'Leaderboard', icon: Star, color: '#8B5CF6',
      description: 'See how you rank against other youth in the program.',
      features: [
        { name: 'Individual Rankings', desc: 'Points-based rankings across all youth in your program.' },
        { name: 'Team Competition', desc: 'If you\'re on a team, see how your team stacks up.' },
      ]
    },
    {
      id: 'ai', title: 'AI Guide', icon: Brain, color: '#7C3AED',
      description: 'Same AI mentor — personalized for your summer job experience.',
      features: [
        { name: 'Career Guidance', desc: 'Ask about careers, job skills, what to do with your summer experience.' },
        { name: 'Profile Breakdown', desc: 'See your Identity Map and what the AI knows about you.' },
      ]
    },
  ];

  const getSections = () => {
    if (selectedModel === 'gps') return GPS_SECTIONS;
    if (selectedModel === 'syep') return SYEP_SECTIONS;
    // For other models, show a simplified version
    return GPS_SECTIONS.filter(s => ['home', 'plan', 'portfolio', 'ai'].includes(s.id));
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8FAFC' }}>
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #F5F3EF 0%, #111827 50%, #1A2236 100%)' }}>
        <div className="relative max-w-4xl mx-auto px-6 py-12 md:py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center overflow-hidden bg-white/10">
              <img src="/yas-connect-logo.png" alt="YAS Connect" className="h-14" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              How to Use YAS Connect
            </h1>
            <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
              Your complete guide to navigating the platform. Select your program below to see exactly what you can do and how to do it.
            </p>
            <Button onClick={() => navigate('/login')} className="bg-white text-indigo-700 hover:bg-gray-100 font-bold px-6 py-3 rounded-xl">
              <LogIn className="w-4 h-4 mr-2" /> Go to Login
            </Button>
          </motion.div>
        </div>
        <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-orange-500/10 blur-3xl" />
        <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Program Selector — Compact horizontal scroll on mobile */}
        <h2 className="text-xl font-bold text-gray-900 mb-4 text-center">Select Your Program</h2>
        <div className="flex gap-3 overflow-x-auto pb-4 mb-6 snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
          {MODELS.map(m => (
            <motion.button key={m.id} onClick={() => selectModel(m.id)}
              whileTap={{ scale: 0.95 }}
              className={`flex-shrink-0 w-[140px] p-4 rounded-2xl text-left snap-start transition-all ${selectedModel === m.id ? 'ring-2 shadow-lg scale-[1.02]' : 'hover:shadow-md'}`}
              style={{
                backgroundColor: selectedModel === m.id ? `${m.color}10` : 'white',
                border: `2px solid ${selectedModel === m.id ? m.color : '#E5E7EB'}`,
                ringColor: m.color
              }}
              data-testid={`select-model-${m.id}`}
            >
              <span className="text-2xl mb-2 block">{m.icon}</span>
              <p className="font-bold text-gray-900 text-xs leading-tight">{m.name}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{m.desc}</p>
            </motion.button>
          ))}
        </div>

        {/* Guide Content — appears immediately after selection */}
        <div ref={guideRef}>
        <AnimatePresence mode="wait">
          {selectedModel && (
            <motion.div key={selectedModel} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              {/* Model Header */}
              <div className="p-6 rounded-2xl mb-6" style={{ background: `linear-gradient(135deg, ${MODELS.find(m => m.id === selectedModel)?.color}15, ${MODELS.find(m => m.id === selectedModel)?.color}05)` }}>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {MODELS.find(m => m.id === selectedModel)?.icon} {MODELS.find(m => m.id === selectedModel)?.name}
                </h2>
                <p className="text-gray-600">{MODELS.find(m => m.id === selectedModel)?.tagline}</p>
              </div>

              {/* Getting Started */}
              <Card className="p-5 rounded-2xl bg-white shadow-sm border-0 mb-6">
                <h3 className="font-bold text-lg text-gray-900 mb-3 flex items-center gap-2">
                  <Play className="w-5 h-5 text-green-500" /> Getting Started
                </h3>
                <div className="space-y-3">
                  {[
                    { step: '1', text: 'You\'ll receive a welcome email with your login credentials (email + temporary password)', icon: '📧' },
                    { step: '2', text: 'Go to the login page and sign in. You may be asked to change your password on first login.', icon: '🔐' },
                    { step: '3', text: 'Complete any required agreements (Terms of Service, Community Guidelines)', icon: '📋' },
                    { step: '4', text: 'You\'re in! Start exploring your dashboard and follow the AI Guide\'s suggestions.', icon: '🚀' },
                  ].map(s => (
                    <div key={s.step} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                      <span className="text-lg">{s.icon}</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Step {s.step}</p>
                        <p className="text-sm text-gray-600">{s.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Page-by-Page Guide */}
              <h3 className="font-bold text-lg text-gray-900 mb-4">Page-by-Page Guide</h3>
              <div className="space-y-3">
                {getSections().map(section => (
                  <Card key={section.id} className="rounded-2xl bg-white shadow-sm border-0 overflow-hidden">
                    <button onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                      className="w-full p-5 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors"
                      data-testid={`section-${section.id}`}>
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${section.color}15` }}>
                        <section.icon className="w-5 h-5" style={{ color: section.color }} />
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-gray-900">{section.title}</p>
                        <p className="text-xs text-gray-500">{section.description}</p>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${expandedSection === section.id ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {expandedSection === section.id && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden">
                          <div className="px-5 pb-5 space-y-3">
                            {section.features.map((f, i) => (
                              <div key={i} className="flex items-start gap-3 p-3 rounded-xl" style={{ backgroundColor: `${section.color}05`, border: `1px solid ${section.color}15` }}>
                                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: section.color }} />
                                <div>
                                  <p className="text-sm font-bold text-gray-900">{f.name}</p>
                                  <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                ))}
              </div>

              {/* Tips */}
              <Card className="p-5 rounded-2xl bg-white shadow-sm border-0 mt-6">
                <h3 className="font-bold text-lg text-gray-900 mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" /> Tips for Success
                </h3>
                <div className="space-y-2">
                  {[
                    'Start by adding your interests — it only takes a minute and unlocks personalized career matches',
                    'Check in weekly — consistency builds your streak and earns you more points',
                    'Talk to the AI Guide whenever you feel stuck or unsure — it knows your full profile',
                    'Set at least one goal — even a small one. Completing goals earns the biggest points',
                    'Your portfolio builds automatically as you use the platform — every interest, goal, and career you explore is saved',
                  ].map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 p-2">
                      <Star className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="currentColor" />
                      <p className="text-sm text-gray-700">{tip}</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Need Help */}
              <Card className="p-5 rounded-2xl mt-6 text-center" style={{ background: 'linear-gradient(135deg, #EDE9FE, #FEF3C7)' }}>
                <Brain className="w-10 h-10 mx-auto mb-3 text-orange-500" />
                <h3 className="font-bold text-gray-900 mb-2">Still have questions?</h3>
                <p className="text-sm text-gray-600 mb-4">Once you're logged in, use the Help Desk to ask your counselor or coach anything. You can also talk to the AI Guide 24/7.</p>
                <Button onClick={() => navigate('/login')} className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl px-6">
                  <LogIn className="w-4 h-4 mr-2" /> Sign In Now
                </Button>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* If no model selected */}
        {!selectedModel && (
          <div className="text-center py-12">
            <Compass className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg text-gray-500">Select your program above to see the guide</p>
          </div>
        )}
        </div>{/* close guideRef */}
      </div>

      {/* Footer */}
      <div className="text-center py-8 border-t border-gray-100">
        <p className="text-sm text-gray-400">YAS Connect — Youth Achievement & Support Platform</p>
      </div>
    </div>
  );
};

export default HowToUsePage;
